import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { validate } from '../contracts/validate';
import { log } from '../utils/DebugLogger';

import '../contracts/Ocorrencias_SaveInput.ts';
import '../contracts/Ocorrencias_SaveResponse.ts';
import '../contracts/Ocorrencias_BuscarOcorrenciasInput.ts';
import '../contracts/Ocorrencias_BuscarOcorrenciasResponse.ts';
import '../contracts/Ocorrencias_DeleteInput.ts';
import '../contracts/Ocorrencias_BuscarOcorrenciasResponse';
import '../contracts/Ocorrencias_DeleteResponse';
import '../contracts/Ocorrencias_SaveResponse';

import type { Ocorrencias_BuscarOcorrenciasInput } from '../contracts/Ocorrencias_BuscarOcorrenciasInput';
import type { Ocorrencias_DeleteInput } from '../contracts/Ocorrencias_DeleteInput';
import type { Ocorrencias_SaveInput } from '../contracts/Ocorrencias_SaveInput';
/**
 * Composable for Ocorrências (Occurrences/Notes) operations
 * Provides methods to interact with the Ocorrências API
 */
export function useOcorrencias() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Fetch occurrences with filters
   * @param {Object} filters - Filter criteria (owner, owner_id, etc.)
   */
  const fetchOcorrencias = async (filters: Ocorrencias_BuscarOcorrenciasInput) => {
    log('useOcorrencias', 'fetchOcorrencias', 'Fetching occurrences', filters);
    isLoading.value = true;
    error.value = null;

    try {
      validate('Ocorrencias_BuscarOcorrenciasInput', filters);
      const response = await httpService.post('/index.php/ocorrencias/buscarOcorrencias', filters, {
        responseContract: 'Ocorrencias_BuscarOcorrenciasResponse'
      });
      log('useOcorrencias', 'fetchOcorrencias', 'Response', response);

      if (response.success) {
        return response.dados || [];
      }

      throw new Error(response.error || 'Erro ao buscar ocorrências');
    } catch (err) {
      log('useOcorrencias', 'fetchOcorrencias', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Save occurrence (create or update)
   * @param {Object} ocorrenciaData - Occurrence data
   */
  const saveOcorrencia = async (ocorrenciaData: Ocorrencias_SaveInput) => {
    log('useOcorrencias', 'saveOcorrencia', 'Saving occurrence', ocorrenciaData);
    isLoading.value = true;
    error.value = null;

    try {
      validate('Ocorrencias_SaveInput', ocorrenciaData);
      const response = await httpService.post('/index.php/ocorrencias/save', ocorrenciaData, {
        responseContract: 'Ocorrencias_SaveResponse'
      });
      log('useOcorrencias', 'saveOcorrencia', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao salvar ocorrência');
      }
    } catch (err) {
      log('useOcorrencias', 'saveOcorrencia', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete occurrence
   * @param {string|number} ocorrenciaId - Occurrence ID (UUID; nunca converta para número)
   */
  const deleteOcorrencia = async (ocorrenciaId: number | string) => {
    log('useOcorrencias', 'deleteOcorrencia', 'Deleting occurrence', ocorrenciaId);
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Ocorrencias_DeleteInput = { ocorrencia_id: String(ocorrenciaId) };
      validate('Ocorrencias_DeleteInput', payload);
      const response = await httpService.post('/index.php/ocorrencias/delete', payload, {
        responseContract: 'Ocorrencias_DeleteResponse',
      });
      log('useOcorrencias', 'deleteOcorrencia', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao deletar ocorrência');
      }
    } catch (err) {
      log('useOcorrencias', 'deleteOcorrencia', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    error,

    // Methods
    fetchOcorrencias,
    saveOcorrencia,
    deleteOcorrencia
  };
}
